import React, { useState, useEffect } from "react";
import { API_BASE } from "../apiConfig";
import "./UsersManagement.css";
import Modal from "./Modal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faTrashAlt, faCheck } from "@fortawesome/free-solid-svg-icons";
import {
  fetchLocations,
  fetchUsers,
  createUser,
  updateUser,
} from "../utils/api";
import PageHeader from "./layout/PageHeader";
import { UserCog } from "lucide-react";

// Map UI roles to Backend Enums
const ROLE_MAPPING = {
  "Read Only": "ReadOnly",
  Editor: "Editor",
  "Full Access": "Admin",
  "Super Admin": "Super Admin", // Keep for legacy/admin
};

const getHighestRole = (accessLevels) => {
  if (!accessLevels) return "ReadOnly";
  const levels = Object.values(accessLevels);

  // Check mapped values
  if (levels.includes("Super Admin") || levels.includes("SuperAdmin"))
    return "Super Admin";
  if (levels.includes("Admin")) return "Admin";
  if (levels.includes("Full Access")) return "Admin";
  if (levels.includes("Editor")) return "Editor";

  return "ReadOnly"; // Default safe role
};

const normalizeRoleLabel = (role = "") => {
  const r = String(role || "").toLowerCase().replace(/[_\s]/g, "");
  if (r === "superadmin") return "Super Admin";
  if (r === "admin" || r === "fullaccess") return "Full Access";
  if (r === "editor") return "Editor";
  return "Read Only";
};

// Feature-scoped access levels — a user checked here can ONLY see/access that
// one feature area, everywhere (sidebar, direct URL, and API — enforced
// server-side by middleware/featureAccessGate.js, not just hidden in the UI).
// Independent of the per-location role above; extensible (add a new entry
// here + FEATURE_ROUTE_PREFIXES in the backend gate for a future level, e.g.
// "WellSky Sync Only" — no other UI change needed).
const FEATURE_ACCESS_OPTIONS = [
  { value: "agent247", label: "Agent247 Only (AI Call Assist)" },
  { value: "ai_hiring", label: "AI Hiring Only" },
];

// Super Admin / Admin-tier accounts can never be feature-restricted — mirrors
// the backend's isAdminLikeRole exemption (routes/users.js) so the option is
// hidden here for the same roles the backend would silently drop it for
// anyway, avoiding a confusing "I selected it but it didn't save" experience.
const isAdminTierHighest = (highestRole) =>
  highestRole === "Super Admin" || highestRole === "Admin";

// Same admin-tier exemption, but against the already-normalized display
// label shown in the Active Users table (user.role), which uses
// normalizeRoleLabel's vocabulary ("Full Access" instead of "Admin").
const isAdminTierDisplayRole = (displayRole) =>
  displayRole === "Super Admin" || displayRole === "Full Access";

const UsersManagement = ({
  activeLocation,
  locations: headerLocations = [],
  onChangeLocation,
  companyDisplayName,
  currentUser: headerUser,
  onLogout,
  onLock,
}) => {
  const normalizePermission = (perm) => {
    const p = (perm || "").toString().toLowerCase().replace(/[_\s]/g, "");
    if (p === "fullaccess" || p === "fullaccesss" || p === "fullaccess") return "Full Access";
    if (p === "editor" || p === "edit") return "Editor";
    return "Read Only";
  };
  const [activeTab, setActiveTab] = useState("active"); // 'active' or 'pending'
  const [usersData, setUsersData] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]); // New state for pending users
  const [featureAccessSavingId, setFeatureAccessSavingId] = useState(null); // userId currently saving inline feature-access edit
  const [featureAccessErrors, setFeatureAccessErrors] = useState({}); // { [userId]: message }
  const [approvalMessage, setApprovalMessage] = useState(""); // To show email sent confirmation
  const [allLocations, setAllLocations] = useState([]); // To populate location options
  const [updating, setUpdating] = useState(false);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteConfirmModalOpen, setIsDeleteConfirmModalOpen] =
    useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [userToDelete, setUserToDelete] = useState(null);

  // Get current user role from localStorage
  const getCurrentUserRole = () => {
    try {
      const userStr = localStorage.getItem("user");
      if (userStr) {
        const user = JSON.parse(userStr);
        return user.role || "VIEW_ONLY";
      }
    } catch (e) {
      console.error("Error parsing user from local storage", e);
    }
    return "VIEW_ONLY";
  };

  const myRole = getCurrentUserRole();
  const canCreate =
    myRole === "EDITOR" ||
    myRole === "FULL_ACCESS" ||
    myRole === "Admin" ||
    myRole === "Super Admin";
  const canApprove = myRole === "FULL_ACCESS" || myRole === "Super Admin";

  // State for Add User Modal
  const [newUserName, setNewUserName] = useState("");
  const [newUserEmail, setNewUserEmail] = useState("");
  const [newSelectedLocations, setNewSelectedLocations] = useState([]);
  const [newAccessLevel, setNewAccessLevel] = useState({});
  // Feature-scoped access ("Agent247 Only" / "AI Hiring Only") — separate,
  // independent of the per-location role/access-level above. See
  // FEATURE_ACCESS_OPTIONS below.
  const [newFeatureAccess, setNewFeatureAccess] = useState([]);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState("");

  // State for Edit User Modal
  const [editUserName, setEditUserName] = useState("");
  const [editUserEmail, setEditUserEmail] = useState("");
  const [editSelectedLocations, setEditSelectedLocations] = useState([]);
  const [editAccessLevel, setEditAccessLevel] = useState({});
  const [editFeatureAccess, setEditFeatureAccess] = useState([]);

  // Resolve location name helper (uses loaded locations)
  const resolveLocName = (locIdOrObj) => {
    if (locIdOrObj && typeof locIdOrObj === "object") {
      const directName = locIdOrObj.name || locIdOrObj.title;
      if (directName) return directName;
      const derivedId =
        locIdOrObj._id || locIdOrObj.id || locIdOrObj.locationId;
      if (derivedId) {
        const found = allLocations.find(
          (l) =>
            (l.id || l._id)?.toString() ===
            (derivedId || "").toString()
        );
        return found?.name || "Unknown location";
      }
      if (typeof locIdOrObj.toString === "function") {
        const found = allLocations.find(
          (l) =>
            (l.id || l._id)?.toString() ===
            locIdOrObj.toString()
        );
        return found?.name || "Unknown location";
      }
      return "Unknown location";
    }
    const locId = locIdOrObj;
    const found = allLocations.find(
      (l) => (l.id || l._id)?.toString() === (locId || "").toString()
    );
    return found?.name || "Unknown location";
  };

  const dedupeLocations = (arr = []) => {
    const seen = new Set();
    return arr.filter((loc) => {
      const key =
        (loc.id || loc._id || loc.locationId || loc.name || "").toString();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  // Fetch Users and Locations on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        // Fetch locations for the dropdown/checkbox list
        const locations = await fetchLocations();
        setAllLocations(locations);
        const resolveLocNameLocal = (locIdOrObj) => {
          if (locIdOrObj && typeof locIdOrObj === "object") {
            return locIdOrObj.name || locIdOrObj.title || "Unknown location";
          }
          const locId = locIdOrObj;
          const found = locations.find(
            (l) => (l.id || l._id)?.toString() === (locId || "").toString()
          );
          return found?.name || "Unknown location";
        };

        // Fetch users with non-care-team account types
        const allUsers = await fetchUsers({
          accountType: "User,Agency,Business,Individual",
        });

        const transformedUsers = allUsers.map((u) => {
          // Build a per-user location name map from populated user.locations.
          // This avoids false "Unknown location" when the logged-in user's
          // own location list does not include another user's location.
          const userLocationNameById = new Map(
            (u.locations || [])
              .map((loc) => {
                const locId =
                  typeof loc === "object" ? loc._id || loc.id : loc;
                const locName =
                  typeof loc === "object" ? loc.name || loc.title : null;
                return [
                  (locId || "").toString(),
                  (locName || "").toString().trim(),
                ];
              })
              .filter(([id, name]) => Boolean(id) && Boolean(name))
          );

          const locAccess = (u.locationAccess || []).map((la) => {
            const locId =
              typeof la.locationId === "object"
                ? la.locationId._id || la.locationId.id
                : la.locationId || la.id || la._id;
            const locName =
              la.name ||
              userLocationNameById.get((locId || "").toString()) ||
              resolveLocNameLocal(locId) ||
              resolveLocNameLocal(typeof locId === "object" ? locId._id || locId.id : locId);
            return {
              id: locId,
              access: normalizePermission(la.permission || la.access || u.role),
              name: locName || "Unknown location",
            };
          });

          // Fallback: if no locationAccess, derive from locations + global role
          const fallbackLocs =
            locAccess.length > 0
              ? locAccess
              : (u.locations || []).map((loc) => {
                  const locId =
                    typeof loc === "object" ? loc._id || loc.id : loc;
                  const locName =
                    (typeof loc === "object" ? loc.name || loc.title : null) ||
                    userLocationNameById.get((locId || "").toString()) ||
                    resolveLocNameLocal(locId);
                  return {
                    id: locId,
                    name: locName || "Unknown location",
                    access: normalizePermission(u.role),
                  };
                });

          const locationAccess =
            fallbackLocs.length > 0
              ? fallbackLocs
                  .map((le) => `${le.name} - ${le.access || "Unknown"}`)
                  .join(", ")
              : "No Access";

          const effectiveAccessMap = fallbackLocs.reduce((acc, le) => {
            if (le?.id) acc[le.id] = le.access || "Read Only";
            return acc;
          }, {});
          const effectiveHighest = getHighestRole(effectiveAccessMap);
          const displayRole = normalizeRoleLabel(
            effectiveHighest && effectiveHighest !== "ReadOnly"
              ? effectiveHighest
              : u.role,
          );

          return {
            id: u._id || u.id,
            name: u.name,
            email: u.email,
            role: displayRole,
            rawRole: u.role, // Preserve DB role for debugging/edits
            featureAccess: Array.isArray(u.featureAccess)
              ? u.featureAccess.filter(Boolean)
              : [],
            status: u.status,
            accountType: u.accountType,
            accessedLocations: fallbackLocs,
            locationAccess,
            date: u.createdAt ? new Date(u.createdAt).toLocaleString() : "N/A",
            company: "",
          };
        });

        setUsersData(transformedUsers.filter((u) => u.status === "Active"));
        setPendingUsers(transformedUsers.filter((u) => u.status === "Pending"));
      } catch (err) {
        console.error("Failed to fetch initial data", err);
      }
    };
    loadData();
  }, []); // Reload when tab changes if needed, or just on mount

  const handleOpenAddModal = () => {
    setIsAddModalOpen(true);
  };

  const handleCloseAddModal = () => {
    setIsAddModalOpen(false);
    setNewUserName("");
    setNewUserEmail("");
    setNewSelectedLocations([]);
    setNewAccessLevel({});
    setNewFeatureAccess([]);
  };

  // Helper to validate email
  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  // Check if form is valid
  const isFormValid = () => {
    if (!newUserName.trim()) return false;
    if (!isValidEmail(newUserEmail)) return false;
    if (newSelectedLocations.length === 0) return false;

    // Ensure every selected location has a role
    // (UI defaults to 'Read Only' but good to check)
    return true;
  };

  const handleCreateUser = async (e) => {
    e.preventDefault(); // Prevent default form submission
    setCreateError("");
    setCreating(true);

    // Defensive checks
    if (!isFormValid()) {
      setCreateError("Please fill all required fields correctly.");
      setCreating(false);
      return;
    }

    // Determine Company ID
    let companyId = null;
    try {
      // 1. Try to get from selected location
      if (newSelectedLocations.length > 0) {
        const firstLocId = newSelectedLocations[0];
        const locationObj = allLocations.find(
          (l) => (l.id || l._id) === firstLocId
        );
        if (locationObj && locationObj.companyId) {
          companyId = locationObj.companyId;
        }
      }

      // 2. Fallback to logged-in user's companyId
      if (!companyId) {
        const userStr = localStorage.getItem("user");
        if (userStr) {
          const u = JSON.parse(userStr);
          companyId = u.companyId;
        }
      }
    } catch (err) {
      console.warn("Error deriving companyId", err);
    }

    if (!companyId) {
      console.error("companyId missing. Payload will fail.");
      setCreateError(
        "System Error: Could not determine Company ID. Please refresh and try again."
      );
      setCreating(false);
      return;
    }

    // Determine global role based on granular selections (map to allowed backend enums)
    const highest = getHighestRole(newAccessLevel); // "Super Admin" | "Admin" | "Editor" | "ReadOnly"
    const mappedRole = ROLE_MAPPING[highest] || highest || "ReadOnly";

    const locationAccessPayload = newSelectedLocations.map((locId) => {
      const perm = normalizePermission(newAccessLevel[locId] || "Read Only");
      const locName = resolveLocName(locId);
      return { locationId: locId, permission: perm, name: locName };
    });

    // For debugging
    console.log("Constructing payload...");

    const newUserPayload = {
      name: newUserName,
      email: newUserEmail,
      accountType: "User",
      role: mappedRole,
      locations: newSelectedLocations, // Array of IDs
      locationAccess: locationAccessPayload,
      status: "Active",
      companyId: companyId,
      // Backend re-enforces the admin exemption regardless of what's sent
      // here (routes/users.js resolveFeatureAccessForWrite) — this UI-side
      // guard (isAdminTierHighest below) just keeps the option hidden so an
      // admin never sees a control that silently wouldn't apply.
      ...(isAdminTierHighest(highest) ? {} : { featureAccess: newFeatureAccess }),
    };

    console.log("Create User Payload:", newUserPayload);

    try {
      const createdUser = await createUser(newUserPayload);

      // After success
      const formattedUser = {
        id: createdUser.user.id || createdUser.user._id,
        name: createdUser.user.name,
        email: createdUser.user.email,
        role: createdUser.user.role,
        featureAccess: createdUser.user.featureAccess || [],
        status: createdUser.user.status,
        accountType: createdUser.user.accountType,
        accessedLocations:
          (createdUser.user.locationAccess || []).map((la) => ({
            id: la.locationId || la.id || la._id || la,
            access: normalizePermission(la.permission || createdUser.user.role),
            name: la.name || resolveLocName(la.locationId || la.id || la._id || la),
          })) ||
          [],
        locationAccess:
          (createdUser.user.locationAccess || []).map((la) => ({
            id: la.locationId || la.id || la._id || la,
            access: normalizePermission(la.permission || createdUser.user.role),
            name: la.name || resolveLocName(la.locationId || la.id || la._id || la),
          })) ||
          [],
      };

      setUsersData((prev) => [...prev, formattedUser]);
      handleCloseAddModal();
    } catch (error) {
      const apiMsg =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message;
      const isDupEmail =
        (error?.response?.data?.error === "EMAIL_ALREADY_EXISTS") ||
        (apiMsg || "").includes("E11000") ||
        (apiMsg || "").toLowerCase().includes("duplicate");
      const friendly = isDupEmail
        ? "This email address is already in use. Please use a different email."
        : apiMsg;
      console.error("Error creating user:", apiMsg, error?.response?.data);
      setCreateError(friendly || "Error creating user. Please try again.");
    } finally {
      setCreating(false);
    }
  };

  const handleOpenEditModal = (user) => {
    setCurrentUser(user);
    setEditUserName(user.name);
    setEditUserEmail(user.email);
    const locs = user.accessedLocations || [];
    setEditSelectedLocations(locs.map((loc) => loc.id));
    const initialAccess = {};
    locs.forEach((loc) => {
      initialAccess[loc.id] = normalizePermission(loc.access);
    });
    setEditAccessLevel(initialAccess);
    setEditFeatureAccess(
      Array.isArray(user.featureAccess) ? user.featureAccess.filter(Boolean) : [],
    );
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setCurrentUser(null);
    setEditUserName("");
    setEditUserEmail("");
    setEditSelectedLocations([]);
    setEditAccessLevel({});
    setEditFeatureAccess([]);
  };

  const handleUpdateUser = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (!currentUser) {
      console.error("Update user failed: currentUser is missing.");
      return;
    }
    const userId = currentUser.id || currentUser._id;
    if (!userId) {
      console.error("Update user failed: user id is missing.");
      return;
    }
    setUpdating(true);
    // Map per-location permissions to a backend-accepted role enum
    const highest = getHighestRole(editAccessLevel);
    const globalRole = ROLE_MAPPING[highest] || highest || "ReadOnly";

    const locationAccessPayload = editSelectedLocations.map((locId) => ({
      locationId: locId,
      permission: normalizePermission(editAccessLevel[locId] || "Read Only"),
      name: resolveLocName(locId),
    }));

    const updatePayload = {
      name: editUserName,
      email: editUserEmail,
      role: globalRole, // account-level role
      locations: editSelectedLocations,
      locationAccess: locationAccessPayload,
      status: currentUser.status,
      // Same admin-exemption note as handleCreateUser above.
      ...(isAdminTierHighest(highest) ? { featureAccess: [] } : { featureAccess: editFeatureAccess }),
    };

    try {
      const response = await fetch(
        `${API_BASE}/api/users/${userId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            // Need auth headers here too usually
            ...(localStorage.getItem("token")
              ? { Authorization: `Bearer ${localStorage.getItem("token")}` }
              : {}),
          },
          body: JSON.stringify(updatePayload),
        }
      );

      if (response.ok) {
        const updatedUser = await response.json();

        const locAccess =
          (updatedUser.locationAccess || []).map((la) => {
            const locId =
              typeof la.locationId === "object"
                ? la.locationId._id || la.locationId.id
                : la.locationId || la.id || la._id;
            return {
              id: locId,
              access: normalizePermission(la.permission || la.access || updatedUser.role),
              name: la.name || resolveLocName(locId),
            };
          }) ||
          [];

        const normalizedLocAccess =
          locAccess.length > 0
            ? locAccess
            : (updatedUser.locations || []).map((lid) => ({
                id: lid,
                access: normalizePermission(updatedUser.role),
                name: resolveLocName(lid),
              }));

        const effectiveAccessMap = normalizedLocAccess.reduce((acc, le) => {
          if (le?.id) acc[le.id] = le.access || "Read Only";
          return acc;
        }, {});
        const effectiveHighest = getHighestRole(effectiveAccessMap);
        const displayRole = normalizeRoleLabel(
          effectiveHighest && effectiveHighest !== "ReadOnly"
            ? effectiveHighest
            : updatedUser.role,
        );

        // Update state
        setUsersData((prevData) =>
          prevData.map((user) =>
            user.id === currentUser.id
              ? {
                  ...user,
                  name: updatedUser.name,
                  email: updatedUser.email,
                  role: displayRole,
                  rawRole: updatedUser.role,
                  featureAccess: updatedUser.featureAccess || [],
                  accountType: updatedUser.accountType,
                  accessedLocations: normalizedLocAccess,
                  locationAccess: normalizedLocAccess
                    .map((le) => `${le.name} - ${le.access || "Unknown"}`)
                    .join(", "),
                }
              : user
          )
        );
        handleCloseEditModal();
      } else {
        const errorText = await response.text().catch(() => "");
        console.error("Failed to update user:", errorText || response.status);
      }
    } catch (error) {
      console.error("Error updating user:", error);
    } finally {
      setUpdating(false);
    }
  };

  // Inline quick-edit for feature access directly from the Active Users
  // table row, so an admin doesn't have to open the full Edit modal just to
  // toggle a feature restriction on/off for an existing user.
  const handleInlineFeatureAccessToggle = async (user, value) => {
    if (isAdminTierDisplayRole(user.role)) return;
    const current = Array.isArray(user.featureAccess) ? user.featureAccess : [];
    const next = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value];

    setFeatureAccessSavingId(user.id);
    setFeatureAccessErrors((prev) => ({ ...prev, [user.id]: null }));
    try {
      const updated = await updateUser(user.id, { featureAccess: next });
      setUsersData((prevData) =>
        prevData.map((u) =>
          u.id === user.id
            ? { ...u, featureAccess: updated.featureAccess || next }
            : u,
        ),
      );
    } catch (error) {
      console.error("Error updating feature access:", error);
      setFeatureAccessErrors((prev) => ({
        ...prev,
        [user.id]:
          error?.response?.data?.message || "Failed to save. Try again.",
      }));
    } finally {
      setFeatureAccessSavingId(null);
    }
  };

  const handleOpenDeleteConfirmModal = (user) => {
    setUserToDelete(user);
    setIsDeleteConfirmModalOpen(true);
  };

  const handleCloseDeleteConfirmModal = () => {
    setIsDeleteConfirmModalOpen(false);
    setUserToDelete(null);
  };

  const handleDeleteUser = async () => {
    try {
      const response = await fetch(
        `${API_BASE}/api/users/${userToDelete.id}`,
        {
          method: "DELETE",
          headers: {
            ...(localStorage.getItem("token")
              ? { Authorization: `Bearer ${localStorage.getItem("token")}` }
              : {}),
          },
        }
      );

      if (response.ok) {
        setUsersData(usersData.filter((user) => user.id !== userToDelete.id));
        handleCloseDeleteConfirmModal();
      } else {
        console.error("Failed to delete user");
      }
    } catch (error) {
      console.error("Error deleting user:", error);
    }
  };

  const handleNewLocationCheckboxChange = (locationId) => {
    setNewSelectedLocations((prev) =>
      prev.includes(locationId)
        ? prev.filter((id) => id !== locationId)
        : [...prev, locationId]
    );
  };

  const handleNewAccessLevelChange = (locationId, level) => {
    setNewAccessLevel((prev) => ({
      ...prev,
      [locationId]: level,
    }));
  };

  const handleEditLocationCheckboxChange = (locationId) => {
    setEditSelectedLocations((prev) =>
      prev.includes(locationId)
        ? prev.filter((id) => id !== locationId)
        : [...prev, locationId]
    );
  };

  const handleEditAccessLevelChange = (locationId, level) => {
    setEditAccessLevel((prev) => ({
      ...prev,
      [locationId]: level,
    }));
  };

  const handleApproveUser = async (userId) => {
    try {
      const response = await fetch(
        `${API_BASE}/api/users/${userId}/approve`,
        {
          method: "POST",
          headers: {
            ...(localStorage.getItem("token")
              ? { Authorization: `Bearer ${localStorage.getItem("token")}` }
              : {}),
          },
        }
      );

      if (response.ok) {
        // Move from pending to active
        const approvedUser = pendingUsers.find((u) => u.id === userId);

        if (approvedUser) {
          const newUser = {
            ...approvedUser,
            status: "Active",
            // approval likely sets a default role or keeps existing
          };
          setUsersData([...usersData, newUser]);
          setPendingUsers(pendingUsers.filter((u) => u.id !== userId));

          setApprovalMessage(
            `User ${approvedUser.email} has been approved successfully.`
          );
          setTimeout(() => setApprovalMessage(""), 5000);
        }
      }
    } catch (err) {
      console.error("Failed to approve", err);
    }
  };

  return (
    <div className="users-management-container page-shell">
      <PageHeader
        title="Users Management"
        subtitle="Control access and permissions for your team."
        icon={<UserCog className="h-6 w-6 text-white" />}
        activeLocation={activeLocation}
        locations={headerLocations}
        onChangeLocation={onChangeLocation}
        companyDisplayName={companyDisplayName}
        currentUser={headerUser}
        onLogout={onLogout}
        onLock={onLock}
      />
      {activeTab === "active" && canCreate && (
        <div className="page-actions">
          <button
            className="button add-user-button"
            onClick={handleOpenAddModal}
          >
            + Add User
          </button>
        </div>
      )}

      {approvalMessage && (
        <div className="approval-success-banner">
          <FontAwesomeIcon icon={faCheck} /> {approvalMessage}
        </div>
      )}

      <div className="users-tabs">
        <button
          className={`tab-btn ${activeTab === "active" ? "active" : ""}`}
          onClick={() => setActiveTab("active")}
        >
          Active Users
        </button>
        <button
          className={`tab-btn ${activeTab === "pending" ? "active" : ""}`}
          onClick={() => setActiveTab("pending")}
        >
          Pending Approvals{" "}
          {pendingUsers.length > 0 && (
            <span className="badge">{pendingUsers.length}</span>
          )}
        </button>
      </div>

      <div className="users-table-container">
        {activeTab === "active" ? (
          /* ACTIVE USERS TABLE */
          <table>
            <thead>
              <tr>
                <th>User</th>
                <th>Email</th>
                <th>Location Access</th>
                <th>Feature Access</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {usersData.map((user) => (
                <tr key={user.id}>
                  <td>
                    <div className="user-info">
                      <div className="user-avatar">
                        {user.name
                          .split(" ")
                          .map((n) => n[0])
                          .join("")}
                      </div>
                      <div>
                        <span className="user-name">{user.name}</span>
                        <br />
                        <span className="user-role">{user.role}</span>
                      </div>
                    </div>
                  </td>
                  <td>{user.email}</td>
                  <td>
                    {dedupeLocations(user.accessedLocations || []).map(
                      (loc, index) => (
                        <span
                          key={index}
                          className={`access-level-badge ${(
                            loc.access || "read-only"
                          )
                            .toLowerCase()
                            .replace(" ", "-")}`}
                        >
                          {loc.name || resolveLocName(loc.id || loc._id) || "Unknown location"}
                        </span>
                      )
                    )}
                  </td>
                  <td>
                    {isAdminTierDisplayRole(user.role) ? (
                      <span className="feature-access-inline-na">
                        Not restrictable (Admin)
                      </span>
                    ) : (
                      <div className="feature-access-inline">
                        {FEATURE_ACCESS_OPTIONS.map((opt) => (
                          <label
                            key={opt.value}
                            className="feature-access-inline-item"
                          >
                            <input
                              type="checkbox"
                              checked={(user.featureAccess || []).includes(
                                opt.value,
                              )}
                              disabled={featureAccessSavingId === user.id}
                              onChange={() =>
                                handleInlineFeatureAccessToggle(user, opt.value)
                              }
                            />
                            {opt.label}
                          </label>
                        ))}
                        {featureAccessSavingId === user.id && (
                          <span className="feature-access-inline-saving">
                            Saving...
                          </span>
                        )}
                        {featureAccessErrors[user.id] && (
                          <span className="feature-access-inline-error">
                            {featureAccessErrors[user.id]}
                          </span>
                        )}
                      </div>
                    )}
                  </td>
                  <td>
                    <span
                      className={`status-badge ${(user.status || "pending")
                        .toLowerCase()
                        .replace(" ", "-")}`}
                    >
                      {user.status}
                    </span>
                  </td>
                  <td>
                    <div
                      style={{
                        display: "flex",
                        gap: "10px",
                        justifyContent: "center",
                      }}
                    >
                      <button
                        className="action-button edit-button"
                        onClick={() => handleOpenEditModal(user)}
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </button>
                      <button
                        className="action-button delete-button"
                        onClick={() => handleOpenDeleteConfirmModal(user)}
                      >
                        <FontAwesomeIcon icon={faTrashAlt} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          /* PENDING USERS TABLE */
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Company</th>
                <th>Registration Date</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {pendingUsers.map((user) => (
                <tr key={user.id}>
                  <td>{user.name}</td>
                  <td>{user.email}</td>
                  <td>{user.company || "-"}</td>
                  <td>{user.date}</td>
                  <td>
                    {canApprove && (
                      <button
                        className="approve-btn"
                        onClick={() => handleApproveUser(user.id)}
                      >
                        <FontAwesomeIcon icon={faCheck} /> Approve
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {pendingUsers.length === 0 && (
                <tr>
                  <td
                    colSpan="5"
                    style={{
                      textAlign: "center",
                      padding: "30px",
                      color: "#888",
                    }}
                  >
                    No pending approvals.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Create New User Modal */}
      <Modal
        isOpen={isAddModalOpen}
        onClose={handleCloseAddModal}
        title="Create New User"
      >
        <form onSubmit={handleCreateUser}>
          <div className="form-section-title">
            <h3>User Information</h3>
          </div>
          <div className="form-group">
            <label>Full Name *</label>
            <input
              type="text"
              value={newUserName}
              onChange={(e) => setNewUserName(e.target.value)}
              required
            />
          </div>
          <div className="form-group">
            <label>Email Address *</label>
            <input
              type="email"
              value={newUserEmail}
              onChange={(e) => setNewUserEmail(e.target.value)}
              required
            />
          </div>

          <div className="form-section-title location-access-title">
            <h3>
              Location Access Permissions{" "}
              <span className="selected-count">
                {newSelectedLocations.length} selected
              </span>
            </h3>
          </div>
          <div className="location-access-list">
            {allLocations.map((location) => (
              <div
                key={location.id || location._id}
                className="location-access-item"
              >
                <input
                  type="checkbox"
                  id={`new-location-${location.id || location._id}`}
                  checked={newSelectedLocations.includes(
                    location.id || location._id
                  )}
                  onChange={() =>
                    handleNewLocationCheckboxChange(location.id || location._id)
                  }
                />
                <label htmlFor={`new-location-${location.id || location._id}`}>
                  {location.name}
                </label>
                {newSelectedLocations.includes(location.id || location._id) && (
                  <div className="access-level-control">
                    <select
                      value={
                        newAccessLevel[location.id || location._id] ||
                        "Read Only"
                      }
                      onChange={(e) =>
                        handleNewAccessLevelChange(
                          location.id || location._id,
                          e.target.value
                        )
                      }
                    >
                      <option value="Read Only">Read Only</option>
                      <option value="Editor">Editor</option>
                      <option value="Full Access">Full Access</option>
                    </select>
                    <span
                      className={`current-access-level ${(
                        newAccessLevel[location.id || location._id] ||
                        "Read Only"
                      )
                        .toLowerCase()
                        .replace(" ", "-")}`}
                    >
                      {newAccessLevel[location.id || location._id] ||
                        "Read Only"}
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>

          {!isAdminTierHighest(getHighestRole(newAccessLevel)) && (
            <>
              <div className="form-section-title">
                <h3>Restrict to Feature Area (optional)</h3>
              </div>
              <p className="form-helper-text">
                Select one to limit this user to ONLY that section of the app —
                no other pages, menus, or data will be visible or reachable,
                regardless of the location access above. Leave unchecked for
                normal access.
              </p>
              <div className="feature-access-list">
                {FEATURE_ACCESS_OPTIONS.map((opt) => (
                  <div key={opt.value} className="feature-access-item">
                    <input
                      type="checkbox"
                      id={`new-feature-${opt.value}`}
                      checked={newFeatureAccess.includes(opt.value)}
                      onChange={() =>
                        setNewFeatureAccess((prev) =>
                          prev.includes(opt.value)
                            ? prev.filter((v) => v !== opt.value)
                            : [...prev, opt.value],
                        )
                      }
                    />
                    <label htmlFor={`new-feature-${opt.value}`}>{opt.label}</label>
                  </div>
                ))}
              </div>
            </>
          )}

          {createError && (
            <div className="approval-success-banner error">{createError}</div>
          )}
          <div className="modal-actions">
            <button
              type="button"
              className="button cancel-button"
              onClick={handleCloseAddModal}
            >
              CANCEL
            </button>
            <button
              type="submit"
              className={`button create-button ${
                !isFormValid() ? "disabled" : ""
              }`}
              disabled={!isFormValid() || creating}
            >
              {creating ? "Creating..." : "CREATE USER"}
            </button>
          </div>
        </form>
      </Modal>

      {/* Edit User Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={handleCloseEditModal}
        title="Edit User"
      >
        <form onSubmit={handleUpdateUser}>
          <div className="form-section-title">
            <h3>User Information</h3>
          </div>
          <div className="form-group">
            <label>Full Name *</label>
            <input
              type="text"
              value={editUserName}
              onChange={(e) => setEditUserName(e.target.value)}
              required
            />
          </div>
          <div className="form-group">
            <label>Email Address *</label>
            <input
              type="email"
              value={editUserEmail}
              onChange={(e) => setEditUserEmail(e.target.value)}
              required
            />
          </div>

          <div className="form-section-title location-access-title">
            <h3>
              Location Access Permissions{" "}
              <span className="selected-count">
                {editSelectedLocations.length} selected
              </span>
            </h3>
          </div>
          <div className="location-access-list">
            {allLocations.map((location) => (
              <div
                key={location.id || location._id}
                className="location-access-item"
              >
                <input
                  type="checkbox"
                  id={`edit-location-${location.id || location._id}`}
                  checked={editSelectedLocations.includes(
                    location.id || location._id
                  )}
                  onChange={() =>
                    handleEditLocationCheckboxChange(
                      location.id || location._id
                    )
                  }
                />
                <label htmlFor={`edit-location-${location.id || location._id}`}>
                  {location.name}
                </label>
                {editSelectedLocations.includes(
                  location.id || location._id
                ) && (
                  <div className="access-level-control">
                    <select
                      value={
                        editAccessLevel[location.id || location._id] ||
                        "Read Only"
                      }
                      onChange={(e) =>
                        handleEditAccessLevelChange(
                          location.id || location._id,
                          e.target.value
                        )
                      }
                    >
                      <option value="Read Only">Read Only</option>
                      <option value="Editor">Editor</option>
                      <option value="Full Access">Full Access</option>
                    </select>
                  </div>
                )}
              </div>
            ))}
          </div>

          {!isAdminTierHighest(getHighestRole(editAccessLevel)) && (
            <>
              <div className="form-section-title">
                <h3>Restrict to Feature Area (optional)</h3>
              </div>
              <p className="form-helper-text">
                Select one to limit this user to ONLY that section of the app —
                no other pages, menus, or data will be visible or reachable,
                regardless of the location access above. Leave unchecked for
                normal access.
              </p>
              <div className="feature-access-list">
                {FEATURE_ACCESS_OPTIONS.map((opt) => (
                  <div key={opt.value} className="feature-access-item">
                    <input
                      type="checkbox"
                      id={`edit-feature-${opt.value}`}
                      checked={editFeatureAccess.includes(opt.value)}
                      onChange={() =>
                        setEditFeatureAccess((prev) =>
                          prev.includes(opt.value)
                            ? prev.filter((v) => v !== opt.value)
                            : [...prev, opt.value],
                        )
                      }
                    />
                    <label htmlFor={`edit-feature-${opt.value}`}>{opt.label}</label>
                  </div>
                ))}
              </div>
            </>
          )}

          <div className="modal-actions">
            <button
              type="button"
              className="button cancel-button"
              onClick={handleCloseEditModal}
            >
              CANCEL
            </button>
            <button
              type="submit"
              className="button create-button"
              onClick={handleUpdateUser}
              disabled={updating}
            >
              {updating ? "UPDATING..." : "UPDATE USER"}
            </button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={isDeleteConfirmModalOpen}
        onClose={handleCloseDeleteConfirmModal}
        title="Confirm Deletion"
      >
        <p>
          Are you sure you want to delete user "{userToDelete?.name}"? This
          action cannot be undone.
        </p>
        <div className="modal-actions">
          <button
            type="button"
            className="button cancel-button"
            onClick={handleCloseDeleteConfirmModal}
          >
            CANCEL
          </button>
          <button
            type="button"
            className="button delete-button"
            onClick={handleDeleteUser}
          >
            DELETE
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default UsersManagement;
